import { useContext, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import NotificationPopup from "./dialog/NotificationPopup";
import AppNav from "./AppNav";
import TopNav from "./main/component/TopNav";
import { useSelector } from "react-redux";
import ActivityDialog from "./dialog/ActivityDialog";
import RightPanelDialog from "./dialog/RightPanelDialog";
import styled from "styled-components";
import ItemDialog from "./dialog/ItemDialog";
import UpgradePlanModal from "./dialog/UpgradePlanModal";
import { ContentTypes } from "./constants/action-types";
import { AppContext } from "./Contexts";
import ImpersonationBanner from "./main/component/ImpersonationBanner";
import { useCrmChatLayoutInset } from "./chat/CrmChatLayoutContext";

export function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const StyledLayout = styled.div`
  .information-tooltip {
    padding: 0;
  }

  .react-tooltip-arrow {
    width: 0;
  }

  .crm-tooltip {
    z-index: 10000 !important;
    position: relative;
  }

  .crm-tooltip[style*="transparent"] {
    background-color: transparent !important;
    background: transparent !important;
  }

  .react-tooltip {
    z-index: 10000 !important;
    position: fixed !important;
    transform: none !important;
    transition: opacity 150ms ease-in-out !important;
  }

  .crm-tooltip {
    position: fixed !important;
    transform: none !important;
    transition: opacity 150ms ease-in-out !important;
    will-change: opacity !important;
  }

  .crm-tooltip[data-tooltip-content] {
    transform: none !important;
  }

  .crm-tooltip * {
    transform: none !important;
  }

  .crm-tooltip > div {
    overflow: visible !important;
    position: static !important;
    transform: none !important;
  }

  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const LeftPanelContainer = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  height: 100%;
  width: 0px;
  overflow: hidden;
  background-color: rgb(255, 255, 255);
  box-shadow:
    rgb(0 0 0 / 22%) 0px 25.6px 57.6px 0px,
    rgb(0 0 0 / 18%) 0px 4.8px 14.4px 0px;
  transition: transform 300ms ease-in-out;
  z-index: 1;
`;

export default function AppLayout({ children }) {
  const { projectId = "" }: any = useParams();
  const location = useLocation();
  const appContext = useContext<any>(AppContext);

  const [showAppNavDialog, setShowAppNavDialog] = useState(false);

  const showAppNav = useSelector((state: any) => state.layoutReducer.get("appNav"));
  window.workspaceId = projectId;

  const showSale =
    useSelector((state: any) =>
      state.taskReducer.getIn(["showTaskDialog", ContentTypes.SALE, "show"])
    ) || false;
  const showContact =
    useSelector((state: any) =>
      state.taskReducer.getIn(["showTaskDialog", ContentTypes.CONTACT, "show"])
    ) || false;
  const showCompany =
    useSelector((state: any) =>
      state.taskReducer.getIn(["showTaskDialog", ContentTypes.COMPANY, "show"])
    ) || false;
  const showProduct =
    useSelector((state: any) =>
      state.taskReducer.getIn(["showTaskDialog", ContentTypes.PRODUCT, "show"])
    ) || false;

  const showNav = projectId !== "";
  const isAddNewPipeline = location.pathname === "/workspaces/new";
  const isIssueApp = appContext.appName == "issueApp";
  const isChrome = appContext.appName == "chrome";
  const crmChatInset = useCrmChatLayoutInset();
  const mainColumnStyle = crmChatInset > 0 ? { marginRight: crmChatInset } : undefined;

  return (
    <StyledLayout className={`text-sm w-full ${isChrome ? "h-[1225px]" : "h-screen"}`}>
      {isChrome ? (
        <div className="w-full flex overflow-hidden">
          <div
            className="flex-1 min-w-0 transition-[margin-right] duration-200 ease-out"
            style={mainColumnStyle}
          >
            {showNav && <TopNav />}
            <div
              className={`${
                isAddNewPipeline ? "h-full" : "h-[calc(1225px-96px)] md:h-[calc(1225px-48px)]"
              } overflow-y-auto`}
            >
              {children}
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full flex overflow-hidden">
          {showNav && !showAppNav && (
            <div
              onMouseEnter={() => setShowAppNavDialog(true)}
              className="fixed top-0 left-0 h-full w-1 z-1"
            />
          )}
          <LeftPanelContainer
            onMouseLeave={() => setShowAppNavDialog(false)}
            style={{
              width: showAppNavDialog ? "240px" : "0px",
            }}
          >
            <AppNav show={true} />
          </LeftPanelContainer>
          {showNav && <AppNav show={false} />}
          <div
            className="flex-1 min-w-0 transition-[margin-right] duration-200 ease-out"
            style={mainColumnStyle}
          >
            {showNav && <TopNav />}
            <div
              className={`${
                isIssueApp || isAddNewPipeline
                  ? "h-full"
                  : "h-[calc(100dvh-96px)] md:h-[calc(100dvh-48px)]"
              } overflow-y-auto`}
            >
              {children}
            </div>
          </div>
        </div>
      )}
      <ImpersonationBanner />
      <NotificationPopup />
      <ActivityDialog />
      <RightPanelDialog />
      <ItemDialog show={showSale} contentType={ContentTypes.SALE} />
      <ItemDialog show={showContact} contentType={ContentTypes.CONTACT} />
      <ItemDialog show={showCompany} contentType={ContentTypes.COMPANY} />
      <ItemDialog show={showProduct} contentType={ContentTypes.PRODUCT} />
      <UpgradePlanModal />
    </StyledLayout>
  );
}
